// src/utils/utmTracking.ts
// Marketing attribution capture for UTM, Meta, Google, and TikTok campaign links.

const UTM_STORAGE_KEY = "astroji_utm";
const MAX_RAW_PARAMS = 80;
const MAX_RAW_KEY_LENGTH = 80;
const MAX_RAW_VALUE_LENGTH = 500;

type NullableString = string | null;

export interface UTMData {
  source: NullableString;
  campaign: NullableString;
  adset: NullableString;
  ad: NullableString;
  campaign_code: NullableString;
  utm_source: NullableString;
  utm_medium: NullableString;
  utm_campaign: NullableString;
  utm_content: NullableString;
  utm_term: NullableString;
  utm_id: NullableString;
  campaign_id: NullableString;
  campaign_name: NullableString;
  adset_id: NullableString;
  adset_name: NullableString;
  ad_id: NullableString;
  ad_name: NullableString;
  creative_id: NullableString;
  creative_name: NullableString;
  placement: NullableString;
  site_source_name: NullableString;
  platform_position: NullableString;
  video_id: NullableString;
  video_name: NullableString;
  post_id: NullableString;
  page_id: NullableString;
  fbclid: NullableString;
  fbp: NullableString;
  fbc: NullableString;
  gclid: NullableString;
  ttclid: NullableString;
  landing_page: NullableString;
  source_note: NullableString;
  raw_params: Record<string, string>;
  captured_at: string;
  updated_at?: string;
}

const SENSITIVE_PARAM_KEYS = new Set([
  "address",
  "client_email",
  "client_phone",
  "contact",
  "dob",
  "email",
  "mobile",
  "name",
  "phone",
  "pob",
  "tob",
  "whatsapp",
]);

const NON_ATTRIBUTION_PARAM_KEYS = new Set([
  "booking_step",
  "debug_step",
  "start",
]);

const firstParam = (params: URLSearchParams, keys: string[]) => {
  for (const key of keys) {
    const value = params.get(key);
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return null;
};

const looksLikeMetaId = (value: string | null) =>
  Boolean(value && /^\d{8,}$/.test(value.trim()));

const valueIfMetaId = (value: string | null) => (looksLikeMetaId(value) ? value : null);
const valueIfNotMetaId = (value: string | null) => (value && !looksLikeMetaId(value) ? value : null);

const getCookie = (name: string) => {
  if (typeof document === "undefined") return null;

  const match = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`));

  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
};

const buildFbcFromFbclid = (fbclid: string | null, capturedAt?: string | number | null) => {
  if (!fbclid) return null;
  const parsedTimestamp =
    typeof capturedAt === "number"
      ? capturedAt
      : capturedAt
        ? new Date(capturedAt).getTime()
        : NaN;
  const timestamp = Number.isFinite(parsedTimestamp) ? parsedTimestamp : Date.now();
  return `fb.1.${timestamp}.${fbclid}`;
};

const truncate = (value: string, maxLength: number) =>
  value.length > maxLength ? value.slice(0, maxLength) : value;

const sanitizeRawParams = (params: URLSearchParams) => {
  const rawParams: Record<string, string> = {};
  let count = 0;

  params.forEach((value, key) => {
    if (count >= MAX_RAW_PARAMS) return;

    const normalizedKey = key.trim().toLowerCase();
    if (
      !normalizedKey ||
      SENSITIVE_PARAM_KEYS.has(normalizedKey) ||
      NON_ATTRIBUTION_PARAM_KEYS.has(normalizedKey)
    ) {
      return;
    }

    rawParams[truncate(normalizedKey, MAX_RAW_KEY_LENGTH)] = truncate(
      value.trim(),
      MAX_RAW_VALUE_LENGTH,
    );
    count += 1;
  });

  return rawParams;
};

const hasIncomingAttributionSignal = (data: UTMData) => {
  const hasKnownSignal = Boolean(
    data.source ||
      data.campaign ||
      data.adset ||
      data.ad ||
      data.campaign_code ||
      data.utm_source ||
      data.utm_medium ||
      data.utm_campaign ||
      data.utm_content ||
      data.utm_term ||
      data.utm_id ||
      data.campaign_id ||
      data.adset_id ||
      data.ad_id ||
      data.placement ||
      data.site_source_name ||
      data.fbclid ||
      data.gclid ||
      data.ttclid,
  );

  if (hasKnownSignal) return true;

  return Object.keys(data.raw_params).some((key) => !NON_ATTRIBUTION_PARAM_KEYS.has(key));
};

const buildUTMData = (params: URLSearchParams): UTMData => {
  const utmCampaignParam = firstParam(params, ["utm_campaign"]);
  const utmContentParam = firstParam(params, ["utm_content"]);
  const utmTermParam = firstParam(params, ["utm_term"]);
  const utmAdsetParam = firstParam(params, ["utm_adset"]);
  const utmAdParam = firstParam(params, ["utm_ad"]);

  const campaignId = firstParam(params, [
    "campaign_id",
    "campaignid",
    "meta_campaign_id",
    "fb_campaign_id",
    "utm_campaign_id",
    "utm_id",
  ]) || valueIfMetaId(utmCampaignParam);
  const campaignName = firstParam(params, [
    "campaign_name",
    "campaign",
    "meta_campaign_name",
  ]) || valueIfNotMetaId(utmCampaignParam);
  const adsetId = firstParam(params, [
    "adset_id",
    "adsetid",
    "meta_adset_id",
    "fb_adset_id",
    "utm_adset_id",
  ]) || valueIfMetaId(utmAdsetParam) || valueIfMetaId(utmTermParam);
  const adsetName = firstParam(params, [
    "adset_name",
    "adset",
    "meta_adset_name",
    "utm_adset",
  ]) || valueIfNotMetaId(utmAdsetParam) || valueIfNotMetaId(utmTermParam);
  const adId = firstParam(params, [
    "ad_id",
    "adid",
    "meta_ad_id",
    "fb_ad_id",
    "utm_ad_id",
  ]) || valueIfMetaId(utmAdParam) || valueIfMetaId(utmContentParam);
  const adName = firstParam(params, [
    "ad_name",
    "ad",
    "meta_ad_name",
    "utm_ad",
  ]) || valueIfNotMetaId(utmAdParam) || valueIfNotMetaId(utmContentParam);
  const fbclid = firstParam(params, ["fbclid"]);
  const fbc = firstParam(params, ["fbc"]) || getCookie("_fbc") || buildFbcFromFbclid(fbclid);
  const fbp = firstParam(params, ["fbp"]) || getCookie("_fbp");
  const placement = firstParam(params, ["placement", "utm_placement"]);
  const platformPosition = firstParam(params, ["platform_position"]) || placement;
  const siteSourceName = firstParam(params, [
    "site_source_name",
    "source_platform",
    "utm_source_platform",
  ]);
  const source = firstParam(params, ["source", "utm_source"]) || siteSourceName;
  const campaign = firstParam(params, ["campaign"]) || campaignName || utmCampaignParam;
  const adset = firstParam(params, ["adset"]) || adsetName;
  const ad = firstParam(params, ["ad"]) || adName || utmContentParam;
  const utmSource = firstParam(params, ["utm_source"]) || source;
  const utmMedium = firstParam(params, ["utm_medium"]);
  const utmCampaign = utmCampaignParam || campaignName || campaign;
  const utmContent = utmContentParam || adName || ad;
  const utmTerm = utmTermParam || adsetName || adset;

  return {
    source,
    campaign,
    adset,
    ad,
    campaign_code: firstParam(params, ["campaign_code"]) || campaign || campaignId,
    utm_source: utmSource,
    utm_medium: utmMedium,
    utm_campaign: utmCampaign,
    utm_content: utmContent,
    utm_term: utmTerm,
    utm_id: firstParam(params, ["utm_id"]) || campaignId,
    campaign_id: campaignId,
    campaign_name: campaignName,
    adset_id: adsetId,
    adset_name: adsetName,
    ad_id: adId,
    ad_name: adName,
    creative_id: firstParam(params, ["creative_id", "creativeid", "meta_creative_id"]),
    creative_name: firstParam(params, ["creative_name", "creative", "meta_creative_name"]),
    placement,
    site_source_name: siteSourceName,
    platform_position: platformPosition,
    video_id: firstParam(params, ["video_id", "video_asset_id", "meta_video_id"]),
    video_name: firstParam(params, ["video_name", "video_asset_name", "meta_video_name"]),
    post_id: firstParam(params, ["post_id", "postid"]),
    page_id: firstParam(params, ["page_id", "pageid"]),
    fbclid,
    fbp,
    fbc,
    gclid: firstParam(params, ["gclid"]),
    ttclid: firstParam(params, ["ttclid"]),
    landing_page: typeof window !== "undefined" ? window.location.pathname : null,
    source_note:
      firstParam(params, ["source_note"]) ||
      (typeof document !== "undefined" ? document.referrer || null : null),
    raw_params: sanitizeRawParams(params),
    captured_at: new Date().toISOString(),
  };
};

const mergeFirstTouchData = (existing: UTMData, incoming: UTMData): UTMData => {
  const merged = { ...incoming } as UTMData;
  const mergedRecord = merged as unknown as Record<string, unknown>;

  (Object.keys(existing) as Array<keyof UTMData>).forEach((key) => {
    if (key === "raw_params") return;
    const existingValue = existing[key];
    if (typeof existingValue === "string") {
      if (existingValue.trim()) {
        mergedRecord[key] = existingValue;
      }
      return;
    }
    if (existingValue !== null && existingValue !== undefined) {
      mergedRecord[key] = existingValue;
    }
  });

  merged.raw_params = {
    ...incoming.raw_params,
    ...(existing.raw_params || {}),
  };
  merged.fbclid = existing.fbclid || incoming.fbclid;
  merged.fbp = existing.fbp || incoming.fbp;
  merged.fbc = existing.fbc || incoming.fbc;
  merged.gclid = existing.gclid || incoming.gclid;
  merged.ttclid = existing.ttclid || incoming.ttclid;
  merged.updated_at = new Date().toISOString();

  return merged;
};

const withLatestBrowserIds = (data: UTMData): UTMData => {
  const latestFbp = getCookie("_fbp");
  const latestFbc = getCookie("_fbc") || buildFbcFromFbclid(data.fbclid, data.captured_at);

  return {
    ...data,
    fbp: data.fbp || latestFbp,
    fbc: data.fbc || latestFbc,
  };
};

const normalizeStoredData = (data: Partial<UTMData>): UTMData => ({
  source: data.source || null,
  campaign: data.campaign || null,
  adset: data.adset || null,
  ad: data.ad || null,
  campaign_code: data.campaign_code || data.campaign || data.utm_campaign || null,
  utm_source: data.utm_source || data.source || null,
  utm_medium: data.utm_medium || null,
  utm_campaign: data.utm_campaign || data.campaign || null,
  utm_content: data.utm_content || data.ad || null,
  utm_term: data.utm_term || null,
  utm_id: data.utm_id || data.campaign_id || null,
  campaign_id: data.campaign_id || null,
  campaign_name: data.campaign_name || data.campaign || data.utm_campaign || null,
  adset_id: data.adset_id || null,
  adset_name: data.adset_name || data.adset || data.utm_term || null,
  ad_id: data.ad_id || null,
  ad_name: data.ad_name || data.ad || data.utm_content || null,
  creative_id: data.creative_id || null,
  creative_name: data.creative_name || null,
  placement: data.placement || null,
  site_source_name: data.site_source_name || data.source || data.utm_source || null,
  platform_position: data.platform_position || data.placement || null,
  video_id: data.video_id || null,
  video_name: data.video_name || null,
  post_id: data.post_id || null,
  page_id: data.page_id || null,
  fbclid: data.fbclid || null,
  fbp: data.fbp || null,
  fbc: data.fbc || null,
  gclid: data.gclid || null,
  ttclid: data.ttclid || null,
  landing_page: data.landing_page || null,
  source_note: data.source_note || null,
  raw_params: data.raw_params || {},
  captured_at: data.captured_at || new Date().toISOString(),
  updated_at: data.updated_at,
});

/**
 * Captures campaign parameters from the current URL.
 * First-touch values are preserved, while click/browser IDs can be backfilled later.
 */
export function captureUTMParams(): void {
  if (typeof window === "undefined") return;

  const urlParams = new URLSearchParams(window.location.search);
  const incoming = buildUTMData(urlParams);
  const existing = getStoredUTMData();

  if (existing) {
    const merged = mergeFirstTouchData(existing, incoming);
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(merged));
    return;
  }

  if (!hasIncomingAttributionSignal(incoming)) {
    return;
  }

  sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(incoming));
}

/**
 * Retrieves stored UTM data from sessionStorage.
 * Returns null if no campaign data exists.
 */
export function getStoredUTMData(): UTMData | null {
  if (typeof sessionStorage === "undefined") return null;

  const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
  if (!stored) return null;

  try {
    return withLatestBrowserIds(normalizeStoredData(JSON.parse(stored) as Partial<UTMData>));
  } catch (error) {
    console.error("UTM: Failed to parse stored attribution data", error);
    return null;
  }
}

export function getStoredAttributionPayload(): Record<string, string> {
  const data = getStoredUTMData();
  if (!data) return {};

  const payload: Record<string, string> = {};
  const add = (key: string, value: unknown) => {
    if (typeof value !== "string") return;
    const trimmed = value.trim();
    if (trimmed) payload[key] = trimmed;
  };

  add("source", data.source);
  add("campaign", data.campaign);
  add("adset", data.adset);
  add("ad", data.ad);
  add("campaign_code", data.campaign_code);
  add("utm_source", data.utm_source);
  add("utm_medium", data.utm_medium);
  add("utm_campaign", data.utm_campaign);
  add("utm_content", data.utm_content);
  add("utm_term", data.utm_term);
  add("utm_id", data.utm_id);
  add("utm_adset", data.adset_name || data.adset);
  add("utm_ad", data.ad_name || data.ad);
  add("campaign_id", data.campaign_id);
  add("campaign_name", data.campaign_name);
  add("adset_id", data.adset_id);
  add("adset_name", data.adset_name);
  add("ad_id", data.ad_id);
  add("ad_name", data.ad_name);
  add("creative_id", data.creative_id);
  add("creative_name", data.creative_name);
  add("placement", data.placement);
  add("site_source_name", data.site_source_name);
  add("platform_position", data.platform_position);
  add("video_id", data.video_id);
  add("video_name", data.video_name);
  add("post_id", data.post_id);
  add("page_id", data.page_id);
  add("fbclid", data.fbclid);
  add("fbp", data.fbp);
  add("fbc", data.fbc);
  add("gclid", data.gclid);
  add("ttclid", data.ttclid);
  add("landing_page", data.landing_page);
  add("source_note", data.source_note);
  add("captured_at", data.captured_at);

  if (Object.keys(data.raw_params || {}).length > 0) {
    payload.raw_params_json = JSON.stringify(data.raw_params);
  }

  return payload;
}

/**
 * Clears stored UTM data after checkout if needed.
 */
export function clearUTMData(): void {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.removeItem(UTM_STORAGE_KEY);
}
